import { Component, Inject, OnInit, Optional } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { STATE_PROVIDER, StateProvider, DefaultStateProvider } from '../services/state-provider/state-provider';
import {
  PROPERTY_SETS_DIALOG_ACTIONS,
  PropertySetsDialogActions,
  PropertySetsDialogActionsProps,
  DefaultPropertySetsDialogActions,
} from './property-sets-dialog-actions';
import { PropertySetsState } from '../property-sets-dialog/property-sets-dialog';
import { PropertySetsStateProviderService } from '../property-set-list/property-set-list-state.service';
import { SpinnerLoadingIndicatorComponent } from '@cbre/components';
import { catchError, of } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'miq-property-sets-dialog-actions',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatDialogModule,
    MatTooltipModule,
    SpinnerLoadingIndicatorComponent,
  ],
  styleUrls: ['./property-sets-dialog-actions.component.scss'],
  template: `
    <!-- TITLE & ACTIONS -->
    <div class="fx--padding-20 fx--h">
      <div class="title-container fx--h fx--juc-space-between fx--grow2">
        <div class="fx--h">
          <!-- Back Button -->
          <div *ngIf="hasBackButton" class="fx--h fx--border__right fx--ali-center fx--mr-sm">
            <a id="e2e-ps-back-btn" (click)="handleBack()" class="fx--mr-sm" aria-label="Back to List"
              ><mat-icon>arrow_back</mat-icon>
            </a>
          </div>
          <div class="fx--v">
            <h1 class="fx--font-black-text fx--fontsize-20">{{ title | uppercase }}</h1>
            <div class="subtitle fx--label">{{ subtitle ?? '' }}</div>
          </div>
        </div>
      </div>
      <div class="fx--h fx--ali-center fx--juc-flex-end fx--grow1">
        <!-- Actions (Save, Cancel) -->
        <div *ngIf="showActions; else closeDialog">
          <button id="e2e-ps-cancel-btn" mat-button color="primary" (click)="handleCancel()" class="fx--mr-sm">
            {{ cancelButtonText }}
          </button>
          <button
            id="e2e-ps-save-btn"
            mat-button
            color="primary"
            mat-flat-button
            [disabled]="!canSave"
            (click)="onSave()"
            [matTooltip]="saveTooltipText"
          >
            {{ saveButtonText }}
          </button>
        </div>

        <!-- Close Dialog -->
        <ng-template #closeDialog>
          <mat-icon id="e2e-close-dialog-btn" (click)="close()">close</mat-icon>
        </ng-template>
      </div>
    </div>
    <ng-container *ngIf="isLoading">
      <cbre-spinner-loading-indicator></cbre-spinner-loading-indicator>
    </ng-container>
  `,
})
export class PropertySetsDialogActionsComponent
  implements OnInit, PropertySetsDialogActionsProps, PropertySetsDialogActions<unknown>
{
  public state: unknown;
  public title: string;
  public subtitle?: string;
  public hasBackButton = false;
  public showActions = false;
  public canSave = true;
  public saveButtonText = 'Save';
  public cancelButtonText = 'Cancel';
  public saveTooltipText: string = null;
  public isLoading = false;

  constructor(
    private propertySetSateProvider: PropertySetsStateProviderService,
    public dialogRef: MatDialogRef<PropertySetsDialogActionsComponent>,
    @Optional() @Inject(PROPERTY_SETS_DIALOG_ACTIONS) private dialogActionsService?: PropertySetsDialogActions<unknown>,
    @Optional()
    @Inject(STATE_PROVIDER)
    private dialogStateProvider?: StateProvider<unknown>
  ) {
    this.dialogActionsService = this.dialogActionsService ?? new DefaultPropertySetsDialogActions();
    this.dialogStateProvider = this.dialogStateProvider ?? new DefaultStateProvider();
  }

  ngOnInit() {
    this.canSave = true;
    this.dialogStateProvider.stateChanges$.pipe(untilDestroyed(this)).subscribe((state: PropertySetsState) => {
      this.tickDialogProps(state);
    });
  }

  tickDialogProps(state?: unknown): PropertySetsDialogActionsProps {
    return Object.assign(this, this.dialogActionsService.tickDialogProps(state));
  }

  onSave() {
    if (!this.canSave) return;
    this.isLoading = true;
    this.canSave = false;
    this.handleSave()
      .pipe(
        catchError(() => {
          this.isLoading = false;
          return of(true);
        })
      )
      .subscribe(enableSaveBtn => {
        this.canSave = enableSaveBtn;
        this.isLoading = false;
      });
  }

  handleSave() {
    const state = this.dialogStateProvider.snapshot();
    return this.dialogActionsService.handleSave(state);
  }

  handleCancel(): void {
    this.dialogActionsService.handleCancel(this.state);
  }

  handleBack(): void {
    this.dialogActionsService.handleBack(this.state);
  }

  close() {
    this.propertySetSateProvider.reset();
    this.dialogRef.close();
  }
}
